<template>
  <div class="news-grid">
    <!-- Filtros -->
    <div class="news-grid__filters">
      <span class="news-grid__filter-label">{{ t.filterBy }}</span>
      <ul class="news-grid__tags">
        <li v-for="(tag, idx) in tags" :key="tag.id">
          <button
            type="button"
            class="news-grid__tag"
            :class="{ 'is-active': isActiveTag[idx] }"
            :aria-pressed="isActiveTag[idx] ? 'true' : 'false'"
            @click="toggleTag(idx)"
            :data-label="`${prismicLang === 'en-us' ? 'Category' : 'Categoria'} ${tag.name}`"
          >
            <span class="news-grid__tag-name">{{ tag.name }}</span>
            <!-- X para deseleccionar (solo visible en activo; el click del botón hace toggle) -->
            <span class="news-grid__tag-x" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                <circle cx="11.5" cy="11.5" r="11.5" fill="white"/>
                <path d="M16.8188 6.46875L6.46875 16.8188" stroke="#5B5B5B" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.46875 6.46875L16.8188 16.8188" stroke="#5B5B5B" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </li>
      </ul>
    </div>

    <!-- Loader inicial -->
    <div v-if="isLoading && mappedItems.length === 0" class="news-grid__loader">
      <span class="news-grid__spinner" aria-hidden="true"></span>
    </div>

    <!-- Estado vacío -->
    <div v-else-if="filteredItems.length === 0" class="news-grid__empty">
      <p class="news-grid__empty-msg">{{ hasActiveFilters ? t.noResults : t.noNews }}</p>
      <button v-if="hasActiveFilters" type="button" class="news-grid__clear" @click="clearFilters">
        {{ t.clearFilters }}
      </button>
    </div>

    <!-- Grilla -->
    <div v-else class="news-grid__items">
      <NewsCard v-for="n in filteredItems" :key="n.id" :item="n" />
    </div>
  </div>
</template>

<script>
import Const from '../../constants';
import NewsCard from './NewsCard.vue';
import { mapNewsItem, NEWS_TAGS } from '../helpers/newsHelpers';

export default {
  name: 'NewsGrid',
  components: { NewsCard },
  props: {
    prismicLang: {
      type: String,
      required: true,
      validator: (value) => ['en-us', 'es-es'].includes(value)
    },
    clcSegmentValue: {
      type: String,
      default: ''
    },
    // Se traen todas las noticias en una sola query (100 = máximo de Prismic).
    pageSize: {
      type: Number,
      default: 100
    }
  },
  data() {
    return {
      rawNews: [],
      isLoading: true,
      isActiveTag: NEWS_TAGS.map(() => false)
    };
  },
  computed: {
    t() {
      const texts = {
        'en-us': {
          filterBy: 'Filter by:',
          noResults: 'No news found. Try adjusting or removing the filters.',
          noNews: 'There are no news articles available at the moment. Please check back later.',
          clearFilters: 'Clear all filters'
        },
        'es-es': {
          filterBy: 'Filtrar por:',
          noResults: 'No se encontraron noticias. Prueba ajustar o quitar los filtros.',
          noNews: 'No hay noticias disponibles en este momento. Por favor, vuelve más tarde.',
          clearFilters: 'Limpiar filtros'
        }
      };
      return texts[this.prismicLang] || texts['en-us'];
    },
    tags() {
      const isEN = this.prismicLang === 'en-us';
      return NEWS_TAGS.map(tag => ({
        id: tag.id,
        name: isEN ? tag.nameEN : tag.nameES,
        prismicCategory: tag.prismicCategory
      }));
    },
    hasActiveFilters() {
      return this.isActiveTag.some(active => active);
    },
    mappedItems() {
      return (this.rawNews || [])
        .filter(item => item?.data?.body && item.data.body.length > 0)
        .map(item => mapNewsItem(item, this.prismicLang, this.clcSegmentValue));
    },
    filteredItems() {
      if (!this.hasActiveFilters) {
        return this.mappedItems;
      }
      const selected = this.tags
        .filter((_tag, idx) => this.isActiveTag[idx])
        .map(tag => tag.prismicCategory);
      return this.mappedItems.filter(n => selected.includes(n.rawCategory));
    }
  },
  watch: {
    // Al cambiar el idioma, reiniciar y recargar.
    prismicLang() {
      this.reset();
      this.fetchNews();
    }
  },
  created() {
    this.fetchNews();
  },
  methods: {
    async fetchNews() {
      try {
        this.isLoading = true;
        const options = {
          pageSize: this.pageSize,
          lang: this.prismicLang,
          orderings: '[document.last_publication_date desc]'
        };
        const response = await this.$prismic.client.query([
          this.$prismic.predicate.at(Const.DOCUMENT_TYPE, Const.CLC_NEWS),
          this.$prismic.predicate.any('my.clc_news.targetings.clc_segment', [Const.GLOBAL, this.clcSegmentValue])
        ], options);

        this.rawNews = response?.results || [];
      } catch (error) {
        console.error('METHOD: NewsGrid.fetchNews ==> ', error);
      } finally {
        this.isLoading = false;
      }
    },
    toggleTag(idx) {
      this.$set(this.isActiveTag, idx, !this.isActiveTag[idx]);
    },
    clearFilters() {
      this.isActiveTag = NEWS_TAGS.map(() => false);
    },
    reset() {
      this.rawNews = [];
      this.isActiveTag = NEWS_TAGS.map(() => false);
    }
  }
};
</script>
